import type { RequestReadyForCheckReportSummary } from "../components/RequestReadyForCheckDialog"
import type { HourReportAdminAction, HourReportDayAuditAdminAction } from "../../../hour-reports/api"
import type { TreeGridApprovedHourDateGroupRow, TreeGridApprovedHourRow } from "../rows/types"
import { isTotalHoursRow, isHourReportRow, isDateGroupRow, getDateGroupRows } from "../rows/utils"

type HandlerParams = {
  grid: TGrid
  row: TreeGridApprovedHourDateGroupRow | TreeGridApprovedHourRow
  checked: boolean
  requestReadyForCheck: (row: RequestReadyForCheckReportSummary) => void
  requestReadyForApproval: (row: RequestReadyForCheckReportSummary) => void
  approveHourReport: (args: { id: string; status: "checked" | "approved" }) => unknown
  performHourReportAdminAction: (args: { id: string; action: HourReportAdminAction }) => unknown
  performDayAuditAdminAction: (args: { days: string[]; action: HourReportDayAuditAdminAction }) => unknown
  setDaysApproved: (args: { days: string[]; status: "checked" | "approved" }) => unknown
}

const isDraftReportApproveAttempt = (
  row: TreeGridApprovedHourDateGroupRow | TreeGridApprovedHourRow,
  checked: boolean
) => {
  return isHourReportRow(row) && checked && row.status === "open"
}
const isReadyForCheckApproveAttempt = (
  row: TreeGridApprovedHourDateGroupRow | TreeGridApprovedHourRow,
  checked: boolean
) => {
  return isHourReportRow(row) && checked && row.status === "submitted"
}
const canCheckReport = (row: TreeGridApprovedHourDateGroupRow | TreeGridApprovedHourRow) =>
  isHourReportRow(row) && row.readyForApprovalCanEdit === 1

export const handleChangeApproved = (params: HandlerParams) => {
  const {
    row,
    checked,
    requestReadyForCheck,
    requestReadyForApproval,
    approveHourReport,
    performHourReportAdminAction,
    performDayAuditAdminAction,
    setDaysApproved,
  } = params
  const nextStatus = checked ? "approved" : "checked"

  if (isHourReportRow(row) && row.editableAs === "hour-report-admin") {
    return performHourReportAdminAction({
      id: row.id,
      action: checked ? "approve" : "unapprove",
    })
  }

  if (isDraftReportApproveAttempt(row, checked)) {
    requestReadyForCheck(row)
    return checked ? 0 : 1
  }

  if (isReadyForCheckApproveAttempt(row, checked)) {
    if (canCheckReport(row)) {
      return approveHourReport({
        id: row.id,
        status: nextStatus,
      })
    }

    requestReadyForApproval(row)
    return checked ? 0 : 1
  }

  if (isHourReportRow(row)) {
    return approveHourReport({
      id: row.id,
      status: nextStatus,
    })
  }

  if (isDateGroupRow(row)) {
    if (row.canManageAsHourReportAdmin) {
      return performDayAuditAdminAction({
        days: [row.id],
        action: checked ? "approve" : "unapprove",
      })
    }

    return setDaysApproved({
      days: [row.id],
      status: nextStatus,
    })
  }

  if (isTotalHoursRow(row)) {
    const dateRows = getDateGroupRows(params.grid)
    const dateRowIds = dateRows.map((row) => row.id)
    const canManageAsAdmin = dateRows.some((row) => row.canManageAsHourReportAdmin)
    if (!dateRowIds.length) return

    if (canManageAsAdmin) {
      return performDayAuditAdminAction({
        days: dateRowIds,
        action: checked ? "approve" : "unapprove",
      })
    }

    return setDaysApproved({
      days: dateRowIds,
      status: nextStatus,
    })
  }
}
